import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { GoogleTagManagerData } from '@sc/data-access-models';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TagManagerBase } from '../types';

@Injectable({
  providedIn: 'root',
})
export class GTagManagerService extends TagManagerBase {
  private readonly gTagConfig: GoogleTagManagerData;
  private readonly gTagLoaded = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly configService: ConfigurationService,
    @Inject(DOCUMENT) private readonly doc: Document
  ) {
    super();

    this.gTagConfig = this.configService.getConfig().googleTagManagerData;

    this.initGtag();
  }

  private afterGTagLoaded() {
    return this.gTagLoaded.pipe(
      filter((isLoaded) => !!isLoaded),
      take(1)
    );
  }

  private getDataLayer() {
    const windowWithDataLayer: Window & { dataLayer: any[] } = window as any;
    windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];
    return windowWithDataLayer.dataLayer;
  }

  private initGtag() {
    if (!this.usable()) {
      return;
    }

    this.gTagLoaded
      .pipe(
        filter((isLoaded) => !isLoaded),
        take(1)
      )
      .subscribe(() => {
        this.getDataLayer().push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
        });

        const gTagScript = this.doc.createElement('script');
        gTagScript.id = 'GTMscript';
        gTagScript.async = true;
        gTagScript.src = this.getScriptSrcFromConfig(this.gTagConfig);

        gTagScript.addEventListener('load', () => this.gTagLoaded.next(true));
        gTagScript.addEventListener('error', (err) =>
          console.error(
            'Failed To Load Google Tag Manager with config :',
            this.gTagConfig,
            err
          )
        );

        gTagScript.setAttribute('nonce', 'unsafe-inline');

        this.doc.head.insertBefore(gTagScript, this.doc.head.firstChild);
      });
  }

  private getScriptSrcFromConfig(gTagConfig: GoogleTagManagerData) {
    // Alternate default path that could also work would be : https://www.googletagmanager.com/gtag/js
    const gTagScriptUrl = new URL(
      gTagConfig.gtm_resource_path || 'https://www.googletagmanager.com/gtm.js'
    );

    gTagScriptUrl.searchParams.set('id', gTagConfig.gtm_id);

    if (gTagConfig.gtm_auth) {
      gTagScriptUrl.searchParams.set('gtm_auth', gTagConfig.gtm_auth);
    }

    if (gTagConfig.gtm_preview) {
      gTagScriptUrl.searchParams.set('gtm_preview', gTagConfig.gtm_preview);
    }

    if (gTagConfig.gtm_resource_path) {
      gTagScriptUrl.searchParams.set(
        'gtm_resource_path',
        gTagConfig.gtm_resource_path
      );
    }

    return gTagScriptUrl.href;
  }

  usable() {
    return this.gTagConfig;
  }

  pushTag(infos: any) {
    if (!this.usable()) {
      return;
    }

    this.afterGTagLoaded().subscribe(() => {
      this.getDataLayer().push(infos);
      console.log('GTagManagerService pushTag', infos);
    });
  }
}
